import { formatScore } from './score-format.js';
// View models for the three compact KPIs on a fleet row (ENTRAID-333). Pure.
//
// Three different measures sit side by side, so each one carries its own name:
//  - onGuard score: onGuard's own 0 to 100 result of its security checks.
//  - Secure Score: Microsoft's own score, current points of the maximum, as a percentage.
//  - Readiness: the lowest "% of criteria met" among the frameworks of the latest succeeded compliance report. It is
//    shown only to roles that may read compliance content (the customer's own Overview), never on the operator fleet card.
// A value that is not there is a muted dash with the reason as its tooltip, never a zero.

const finite = (n) => typeof n === 'number' && Number.isFinite(n);

/** Subtle accent only (never red): a dot beside the value. */
function accentFor(percent) {
  if (!finite(percent)) return 'none';
  return percent >= 80 ? 'good' : percent >= 60 ? 'mid' : 'low';
}

export function onGuardKpi(customer) {
  const score = customer?.securityScore;
  if (!finite(score)) {
    return { id: 'onguard', label: 'onGuard score', value: null, text: '--', title: 'onGuard score: not collected yet', accent: 'none' };
  }
  return { id: 'onguard', label: 'onGuard score', value: score, text: formatScore(score), title: `onGuard score: ${formatScore(score)}`, accent: accentFor(score) };
}

/** Microsoft Secure Score from the run summary kept on the customer (msSecureScoreCurrent, msSecureScoreMax, msSecureScoreAsOf). */
export function secureScoreKpi(customer) {
  const current = customer?.msSecureScoreCurrent;
  const max = customer?.msSecureScoreMax;
  if (!finite(current) || !finite(max) || max <= 0) {
    return { id: 'secure-score', label: 'Secure Score', value: null, text: '--', title: 'Microsoft Secure Score: not collected yet', accent: 'none' };
  }
  const percent = Math.round((current / max) * 100);
  const asOf = typeof customer.msSecureScoreAsOf === 'string' && customer.msSecureScoreAsOf ? customer.msSecureScoreAsOf : 'an unknown date';
  return { id: 'secure-score', label: 'Secure Score', value: percent, text: `${percent}%`, title: `Microsoft Secure Score: ${current} of ${max}, as of ${asOf}`, accent: accentFor(percent) };
}

/**
 * @param {{ state: 'loading' } | { state: 'error' } | { state: 'none' } | { state: 'report', frameworks: { name: string, percent: number|null }[] }} input
 */
export function readinessKpi(input) {
  const base = { id: 'readiness', label: 'Readiness', value: null, text: '--', accent: 'none' };
  if (input?.state === 'loading') return { ...base, title: 'Compliance readiness: loading' };
  if (input?.state === 'error') return { ...base, title: 'Compliance readiness: not available right now' };
  if (input?.state !== 'report') return { ...base, title: 'Compliance readiness: no report yet' };
  const entries = (Array.isArray(input.frameworks) ? input.frameworks : []).filter((f) => f && finite(f.percent));
  if (entries.length === 0) return { ...base, title: 'Compliance readiness: no report yet' };
  const lowest = Math.min(...entries.map((f) => f.percent));
  const lines = entries.map((f) => `${f.name}: ${f.percent}%`).join('; ');
  return { id: 'readiness', label: 'Readiness', value: lowest, text: `${lowest}%`, title: `Compliance readiness, lowest of ${entries.length === 1 ? 'the framework' : 'the frameworks'} (${lines})`, accent: accentFor(lowest) };
}

/** The two KPIs the operator fleet card shows; readiness is customer view only. */
export function fleetKpis(customer) {
  return [onGuardKpi(customer), secureScoreKpi(customer)];
}
